/**
 * Purchasing inputs (PRD v2 §6.3–6.4). Shares the spine idempotency envelope
 * and strict decimal parser with opening-stock inputs.
 */
import { z } from 'zod';
import { idempotency } from './spine';
import { parseOpeningQtyHundredths } from '../opening-stock';
import { replyToEmailField } from './org';
import { qtyToHundredths } from '../inventory';

/** How a purchase order came to exist. A counter run is a PO that skipped the ordering. */
export const PO_SOURCES = ['order', 'counter'] as const;
export type PoSource = (typeof PO_SOURCES)[number];
export const PO_FULFILLMENTS = ['deliver', 'will_call'] as const;
export type PoFulfillment = (typeof PO_FULFILLMENTS)[number];
const vendorPartText = z
  .string()
  .trim()
  .max(120)
  .refine(
    (v) => !/[\u0000-\u001f\u007f]/.test(v),
    'Use a single-line vendor part number',
  );

const qty = z.coerce.number().finite().gt(0).max(1_000_000);
const receiptQty = qty.refine(
  (n) => qtyToHundredths(n) > 0,
  'Receipt quantity must round to at least one hundredth.',
);
const completeReceiptKey = (
  p: { clientId?: string; mutationId?: string },
  ctx: z.RefinementCtx,
) => {
  if (Boolean(p.clientId) !== Boolean(p.mutationId))
    ctx.addIssue({
      code: 'custom',
      message: 'Supply both delivery key fields or neither.',
      path: ['mutationId'],
    });
};
const costCents = z.coerce.number().int().min(0).max(100_000_000);
const optionalText = (max: number) =>
  z.string().trim().max(max).optional().or(z.literal(''));

export const vendorCreateInput = idempotency.extend({
  name: z.string().trim().min(1).max(160),
  email: z.string().trim().email().optional().or(z.literal('')),
  phone: optionalText(40),
  accountNumber: optionalText(60),
  termsDays: z.coerce.number().int().min(0).max(365).optional(),
  notes: optionalText(2000),
});
export type VendorCreateInput = z.infer<typeof vendorCreateInput>;

export const vendorUpdateInput = idempotency.extend({
  vendorId: z.string().uuid(),
  name: z.string().trim().min(1).max(160).optional(),
  email: z.string().trim().email().optional().or(z.literal('')),
  phone: optionalText(40),
  accountNumber: optionalText(60),
  termsDays: z.coerce.number().int().min(0).max(365).nullable().optional(),
  notes: optionalText(2000),
  active: z.boolean().optional(),
});
export type VendorUpdateInput = z.infer<typeof vendorUpdateInput>;

export const poLineInput = z.object({
  vendorPartNumber: vendorPartText.optional(),
  itemId: z.string().uuid(),
  qty,
  /** Defaults to the item's last cost when omitted — what the shop paid last time. */
  unitCostCents: costCents.optional(),
});
export type PoLineInput = z.infer<typeof poLineInput>;

export const poCreateInput = idempotency.extend({
  fulfillment: z.enum(PO_FULFILLMENTS).optional(),
  jobId: z.string().uuid().optional(),
  vendorId: z.string().uuid(),
  /** Where it lands when it arrives — the warehouse, or a van for a counter run. */
  locationId: z.string().uuid(),
  expectedDate: z.string().date().optional().or(z.literal('')),
  memo: optionalText(2000),
  lines: z.array(poLineInput).min(1).max(200),
});
export type PoCreateInput = z.infer<typeof poCreateInput>;

export const poIdInput = idempotency.extend({
  purchaseOrderId: z.string().uuid(),
});
export type PoIdInput = z.infer<typeof poIdInput>;

export const poCancelInput = idempotency.extend({
  purchaseOrderId: z.string().uuid(),
  reason: optionalText(500),
});
export type PoCancelInput = z.infer<typeof poCancelInput>;

/**
 * What actually showed up. `unitCostCents` is optional because the price on the packing
 * slip is usually the price on the PO — but when it isn't, the invoice wins, and the
 * moving average has to hear about it.
 */
export const poReceiveInput = idempotency
  .extend({
    purchaseOrderId: z.string().uuid(),
    /** Overrides the PO's location — the pallet went to the yard, not the shelf. */
    locationId: z.string().uuid().optional(),
    lines: z
      .array(
        z.object({
          lineId: z.string().uuid(),
          qty: receiptQty,
          unitCostCents: costCents.optional(),
        }),
      )
      .min(1)
      .max(200),
    note: optionalText(500),
  })
  .superRefine((p, ctx) => {
    completeReceiptKey(p, ctx);
    const seen = new Set<string>();
    p.lines.forEach((line, i) => {
      if (seen.has(line.lineId))
        ctx.addIssue({
          code: 'custom',
          message: 'A purchase order line may appear only once in a receipt.',
          path: ['lines', i, 'lineId'],
        });
      seen.add(line.lineId);
    });
  });
export type PoReceiveInput = z.infer<typeof poReceiveInput>;

/** The supply-house counter run: bought and received in the same breath (§6.3). */
export const quickReceiveInput = idempotency
  .extend({
    fulfillment: z.enum(PO_FULFILLMENTS).optional(),
    vendorId: z.string().uuid(),
    locationId: z.string().uuid(),
    memo: optionalText(2000),
    lines: z
      .array(
        z.object({
          itemId: z.string().uuid(),
          qty: receiptQty,
          unitCostCents: costCents,
          vendorPartNumber: vendorPartText.optional(),
        }),
      )
      .min(1)
      .max(200),
  })
  .superRefine(completeReceiptKey);
export type QuickReceiveInput = z.infer<typeof quickReceiveInput>;

/**
 * One-click replenishment (§6.5): a low-stock list becomes DRAFT orders, grouped by vendor —
 * one per supply house, not one per part. Draft on purpose; the shop still presses send,
 * because a system that emails suppliers on its own is a system nobody trusts twice.
 */
export const draftPosInput = idempotency.extend({
  lines: z
    .array(
      z.object({
        itemId: z.string().uuid(),
        vendorId: z.string().uuid(),
        locationId: z.string().uuid(),
        qty,
      }),
    )
    .min(1)
    .max(200),
});
export type DraftPosInput = z.infer<typeof draftPosInput>;

export const formatPoNumber = (n: number): string => `PO-${n}`;

/** Full editable draft intent; a stale form cannot replace a newer saved order. */
export const poEditInput = idempotency
  .extend({
    purchaseOrderId: z.string().uuid(),
    expectedRevision: z.string().regex(/^[a-f0-9]{64}$/),
    fulfillment: z.enum(PO_FULFILLMENTS).optional(),
    vendorId: z.string().uuid(),
    locationId: z.string().uuid(),
    expectedDate: z.string().date().optional().or(z.literal('')),
    memo: optionalText(2000),
    lines: z
      .array(
        poLineInput
          .extend({
            qty: z
              .number()
              .finite()
              .gt(0)
              .max(1_000_000)
              .refine(
                (n) => parseOpeningQtyHundredths(n) !== null,
                'Use at most two quantity decimals',
              ),
            unitCostCents: z.number().int().min(0).max(100_000_000),
          })
          .strict(),
      )
      .min(1)
      .max(200),
  })
  .strict();
export type PoEditInput = z.infer<typeof poEditInput>;

/** New supplier operations require their own delivery key and reject unknown/attachment fields. */
const poCommand = idempotency
  .extend({
    clientId: z.string().uuid(),
    mutationId: z.string().uuid(),
    purchaseOrderId: z.string().uuid(),
  })
  .strict();
export const poPrepareSendInput = poCommand;
export type PoPrepareSendInput = z.infer<typeof poPrepareSendInput>;
const preparedPoCommand = poCommand
  .extend({
    documentId: z.string().uuid(),
    sourceHash: z.string().regex(/^[a-f0-9]{64}$/),
    expectedRevision: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict();
export const poSendInput = preparedPoCommand
  .extend({
    to: replyToEmailField.pipe(z.string({ error: 'Enter one vendor email address' })),
  })
  .strict();
export type PoSendInput = z.infer<typeof poSendInput>;
export const poManualSendInput = preparedPoCommand
  .extend({
    method: z.string().trim().min(1).max(200),
    note: z.string().trim().min(1).max(2000),
  })
  .strict();
export type PoManualSendInput = z.infer<typeof poManualSendInput>;
export const poResolveSentInput = poCommand
  .extend({
    emailId: z.string().uuid(),
    documentId: z.string().uuid(),
    expectedEmailStateHash: z.string().regex(/^[a-f0-9]{64}$/),
    expectedRevision: z.string().regex(/^[a-f0-9]{64}$/),
    method: z.string().trim().min(1).max(200),
    note: z.string().trim().min(1).max(2000),
  })
  .strict();
export type PoResolveSentInput = z.infer<typeof poResolveSentInput>;
export const vendorPartsInput = z
  .object({ vendorId: z.string().uuid(), itemIds: z.array(z.string().uuid()).max(200) })
  .strict();
export type VendorPartsInput = z.infer<typeof vendorPartsInput>;
export const poDraftDefaultsInput = vendorPartsInput
  .extend({ locationId: z.string().uuid() })
  .strict();
export type PoDraftDefaultsInput = z.infer<typeof poDraftDefaultsInput>;
export const vendorPartSetInput = idempotency
  .extend({
    clientId: z.string().uuid(),
    mutationId: z.string().uuid(),
    vendorId: z.string().uuid(),
    itemId: z.string().uuid(),
    vendorPartNumber: vendorPartText,
  })
  .strict();
export type VendorPartSetInput = z.infer<typeof vendorPartSetInput>;

/** Manager-only status; no provider payload, key, or attachment bytes. */
export interface PoDeliveryView {
  state: 'clear' | 'pending' | 'unconfirmed' | 'accepted';
  locked: boolean;
  canResolve: boolean;
  emailId: string | null;
  documentId: string | null;
  emailStateHash: string | null;
  to: string | null;
  status: string | null;
  waitingFor: string | null;
  failure: string | null;
}

/** Kept separate from permissive legacy draft authoring: malformed plans cannot fall back. */
export { reorderDraftsInput, type ReorderDraftsInput } from './restocks';

export const orderedCostProvenance = z.enum([
  'legacy_unknown',
  'declared_ordered',
  'counter_representation',
]);
export const receivedCostProvenance = z.enum(['declared_this_receipt', 'inherited_line']);
export const receiptClassification = z.enum([
  'above_tolerance',
  'within_tolerance',
  'excluded',
]);
export const receiptExclusion = z.enum([
  'counter',
  'unknown_ordered',
  'price_not_declared',
]);
export const receiptPriceObservation = z
  .object({
    id: z.string().uuid(),
    purchaseOrderId: z.string().uuid(),
    lineId: z.string().uuid(),
    itemId: z.string().uuid(),
    movementId: z.string().uuid(),
    vendorId: z.string().uuid(),
    locationId: z.string().uuid(),
    actorId: z.string().uuid(),
    receivedAt: z.string().datetime(),
    qtyHundredths: z.number().int().min(1).max(100000000),
    recordedUnitCostCents: z.number().int().min(0).max(100000000),
    orderedUnitCostCents: z.number().int().min(0).max(100000000).nullable(),
    orderedCostProvenance,
    receivedCostProvenance,
    toleranceBps: z.number().int().min(0).max(10000),
    classification: receiptClassification,
    exclusionReason: receiptExclusion.nullable(),
    deltaCents: z.number().int().nullable(),
  })
  .strict();
export type ReceiptPriceObservation = z.infer<typeof receiptPriceObservation>;
export const receiptPriceSummary = z.discriminatedUnion('availability', [
  z
    .object({
      availability: z.literal('available'),
      observations: z.array(receiptPriceObservation).max(200),
    })
    .strict(),
  z
    .object({ availability: z.literal('unavailable'), reason: z.literal('legacy_reply') })
    .strict(),
]);
export type ReceiptPriceSummary = z.infer<typeof receiptPriceSummary>;

export const autoPriceChange = z
  .object({
    id: z.string().uuid(),
    itemId: z.string().uuid(),
    itemName: z.string(),
    beforeSellCents: z.number().int().min(0).max(100000000),
    afterSellCents: z.number().int().min(0).max(100000000),
    previousLastCostCents: z.number().int().min(0).max(100000000).nullable(),
    lastCostCents: z.number().int().min(0).max(100000000).nullable(),
  })
  .strict();
export type AutoPriceChange = z.infer<typeof autoPriceChange>;
export const autoPriceSummary = z
  .discriminatedUnion('availability', [
    z
      .object({
        availability: z.literal('available'),
        operationId: z.string().uuid().nullable(),
        raiseCount: z.number().int().min(0).max(200),
        partCount: z.number().int().min(0).max(200),
        changes: z.array(autoPriceChange).max(200),
      })
      .strict(),
    z
      .object({
        availability: z.literal('unavailable'),
        reason: z.literal('legacy_reply'),
      })
      .strict(),
  ])
  .superRefine((s, c) => {
    if (
      s.availability === 'available' &&
      (s.raiseCount !== s.changes.length ||
        s.partCount !== new Set(s.changes.map((x) => x.itemId)).size ||
        Boolean(s.operationId) !== Boolean(s.raiseCount))
    )
      c.addIssue({ code: 'custom', message: 'Inconsistent price summary.' });
  });
export type AutoPriceSummary = z.infer<typeof autoPriceSummary>;
